import { Link } from '@tanstack/react-router'
import { Plus, Minus } from 'lucide-react'
import useSection from '../../hooks/useSection'

interface FAQ {
  question: string
  answer: string
}

const faqs: FAQ[] = [
  {
    question: 'How do you ensure data security?',
    answer:
      'We employ industry-standard encryption protocols (AES-256), strict access controls, and regular security audits. Our development lifecycle incorporates DevSecOps practices to identify and mitigate vulnerabilities early.',
  },
  {
    question: 'What is your typical project timeline?',
    answer:
      'Timelines vary by complexity, but we follow an agile methodology. A typical MVP can be delivered in 8-12 weeks, while larger enterprise transformation projects are rolled out in phased sprints over 6-12 months.',
  },
  {
    question: 'Do you provide post-launch support?',
    answer:
      'Yes, we offer comprehensive maintenance and support packages. This includes 24/7 monitoring, critical bug fixes, performance optimization, and regular feature updates to ensure your software remains ahead of the curve.',
  },
  {
    question: 'Can you integrate with our existing systems?',
    answer:
      'Absolutely. Our architecture is designed for interoperability. We specialize in building custom APIs and middleware to seamlessly connect with your legacy systems, third-party ERPs, CRMs, and cloud tools.',
  },
  {
    question: 'What technologies do you specialize in?',
    answer:
      'We are experts in modern full-stack development using React, Next.js, Node.js, Python, and Go. For cloud infrastructure, we are certified partners with AWS, Azure, and Google Cloud Platform.',
  },
]

interface FAQSectionProps {
  className?: string
}

export default function FAQSection({ className }: FAQSectionProps) {
  const { toggleSection, isActive: isFAQActive } = useSection(0)

  return (
    <section
      id="faq"
      className={`relative z-10 py-24 container mx-auto px-6 md:px-16 border-t border-white/5 ${className}`}
    >
      <div className="grid lg:grid-cols-12 gap-8 md:gap-12 lg:gap-24">
        <div className="md:col-span-5 space-y-6 min-w-0">
          <h2 className="text-4xl md:text-6xl font-bold leading-tight">
            Frequently Asked <br /> Questions
          </h2>
          <p className="text-slate-400 text-lg">
            Everything you need to know about our services, methodology, and how
            we deliver value to your business.
          </p>
          <div className="pt-4">
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 text-indigo-400 hover:text-indigo-300 font-medium transition-colors"
            >
              <span className="text-xl">↳</span> Contact Support
            </Link>
          </div>
        </div>
        <div className="md:col-span-7 space-y-4 min-w-0">
          {faqs.map((faq, index) => (
            <div key={index} className="border-b border-white/10 last:border-0">
              <button
                onClick={() => toggleSection(index)}
                className="w-full py-6 flex items-center justify-between text-left group"
              >
                <span
                  className={`text-xl font-medium transition-colors wrap-break-word ${isFAQActive(index) ? 'text-white' : 'text-slate-300 group-hover:text-white'}`}
                >
                  {faq.question}
                </span>
                <span
                  className={`shrink-0 w-8 h-8 rounded-full border border-white/10 flex items-center justify-center transition-all ${isFAQActive(index) ? 'bg-indigo-500 border-indigo-500 rotate-180' : 'bg-transparent group-hover:border-white/30'}`}
                >
                  {isFAQActive(index) ? (
                    <Minus className="w-4 h-4 text-white" />
                  ) : (
                    <Plus className="w-4 h-4 text-slate-400" />
                  )}
                </span>
              </button>
              <div
                className={`grid transition-all duration-300 ease-in-out ${isFAQActive(index) ? 'grid-rows-[1fr] opacity-100 pb-6' : 'grid-rows-[0fr] opacity-0'}`}
              >
                <div className="overflow-y-hidden">
                  <p className="text-slate-400 leading-relaxed pr-8 wrap-break-word max-w-full">
                    {faq.answer}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  )
}
